import Phaser from 'phaser';
import { APP_VERSION, COLORS, GAME_HEIGHT, GAME_WIDTH, MAP_FRAME, XP_REWARD_MULTIPLIER } from '../config/constants';
import { audio } from '../core/AudioSynth';
import { navigateTo } from '../core/SceneTransition';
import { formatClearStars, getClearStars, getCommandRank, saveManager, type LeaderboardEntry } from '../core/SaveManager';
import { leaderboardService, type LeaderboardSnapshot } from '../core/LeaderboardService';
import { music } from '../core/MusicSystem';
import { localMusic } from '../core/LocalMusicPlayer';
import { exitGame } from '../core/ExitGame';
import { addAudioToggleButtons, addFullscreenIconButton, addRetroButtonHover } from '../ui/Retro';
import { applyUiTextScale } from '../ui/UiText';
import { FieldManualPanel } from '../ui/FieldManualPanel';
import { applyAudioSettings } from '../core/Settings';
import { VOICE_LINES } from '../data/voiceManifest';
import { voice } from '../core/Voice';
import { ENEMIES } from '../data/enemies';
import { getMissionMap, getMissionWaves } from '../data/missions';
import { getNextUnbeatenMissionId } from '../data/missions';
import { getMapRoutes } from '../data/map';
import { mapService } from '../core/MapService';
import { RuntimeAssetsScene } from './RuntimeAssetsScene';
import { INTRO_SEEN_KEY } from './StoryScene';
import { consumeSessionAutoResume, rememberScene, shouldAutoResumeSortie } from '../core/SessionMemory';

export class BootScene extends Phaser.Scene {
  constructor() {
    super('Boot');
  }

  preload(): void {
    // Keep the first paint small. The full tactical art and voice pack is
    // loaded by RuntimeAssetsScene only when the player leaves this menu.
    const loadingBg = this.add.graphics();
    loadingBg.fillStyle(0x07090d, 1).fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    loadingBg.fillStyle(0x0e1a23, 0.94).fillRoundedRect(250, 250, 780, 190, 10);
    loadingBg.lineStyle(1.5, 0x31566a, 1).strokeRoundedRect(250, 250, 780, 190, 10);
    loadingBg.fillStyle(0x5ec8ff, 0.9).fillRect(290, 300, 700, 3);
    const loadingTitle = this.add.text(GAME_WIDTH / 2, 286, 'APOGEE HOLD', {
      fontFamily: 'Consolas, monospace', fontSize: '30px', color: '#c8d4e0', fontStyle: 'bold',
    }).setOrigin(0.5);
    const loadingStatus = this.add.text(GAME_WIDTH / 2, 332, 'INITIALISING COMMAND DECK…', {
      fontFamily: 'Consolas, monospace', fontSize: '12px', color: '#9ad4e8',
    }).setOrigin(0.5);
    const loadingProgress = this.add.text(GAME_WIDTH / 2, 374, '0%', {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#d4a24a',
    }).setOrigin(0.5);
    const progressBar = this.add.graphics();
    const paintProgress = (value: number) => {
      progressBar.clear();
      progressBar.fillStyle(0x152028, 1).fillRect(290, 350, 700, 10);
      progressBar.fillStyle(0x5ec8ff, 0.95).fillRect(290, 350, 700 * Phaser.Math.Clamp(value, 0, 1), 10);
      loadingProgress.setText(`${Math.round(value * 100)}%`);
    };
    paintProgress(0.08);
    this.load.on('progress', paintProgress);
    this.load.on('fileprogress', (file: Phaser.Loader.File) => {
      loadingStatus.setText(`LOADING ${file.key.toUpperCase()}…`);
    });
    // Indeterminate pulse so a quiet loader never reads as frozen.
    const pulse = this.time.addEvent({
      delay: 90,
      loop: true,
      callback: () => {
        const t = (this.time.now / 900) % 1;
        progressBar.clear();
        progressBar.fillStyle(0x152028, 1).fillRect(290, 350, 700, 10);
        progressBar.fillStyle(0x5ec8ff, 0.9).fillRect(290 + t * 520, 350, 180, 10);
      },
    });
    this.load.once('complete', () => {
      pulse.remove(false);
      paintProgress(1);
      this.time.delayedCall(120, () => {
        loadingBg.destroy();
        loadingTitle.destroy();
        loadingStatus.destroy();
        loadingProgress.destroy();
        progressBar.destroy();
      });
    });

    this.load.setBaseURL('');
    this.load.image('menu-keyart', 'assets/art/menu-keyart.png');
    this.load.image('command-emblem', 'assets/art/command-emblem.png');
  }

  async create(): Promise<void> {
    // Warm the tactical pack in the background so mission deploy does not
    // sit on a frozen load bar after the player leaves this menu.
    RuntimeAssetsScene.startInBackground(this);
    // Campaign map edits are shared runtime data. Load them before the first
    // player-facing scene is shown so mission previews and battles agree.
    await mapService.loadCampaignLayouts();
    document.getElementById('bootSplash')?.remove();
    this.scene.start('Menu');
  }
}

export class MenuScene extends Phaser.Scene {
  private difficulty: 'normal' | 'hard' | 'nightmare' = 'normal';
  private keyart?: Phaser.GameObjects.Image;
  private fieldManual?: FieldManualPanel;
  private txtNowPlaying?: Phaser.GameObjects.Text;
  private suppressNextMenuVoice = false;
  private usernameOverlay?: HTMLDivElement;
  private usernameInput?: HTMLInputElement;
  private saveOverlay?: HTMLDivElement;
  private saveCards?: Phaser.GameObjects.Container;
  private firstIntroOpened = false;

  private toggleSaveCards(): void {
    if (!this.saveCards) return;
    this.saveCards.setVisible(!this.saveCards.visible);
  }

  private nowPlayingLabel(): string {
    const local = localMusic.snapshot();
    // The menu card is intentionally compact: the previous all-caps label
    // could escape its panel whenever a song title was longer than a few
    // words. Keep the source and current track, but make its visual boundary
    // reliable at every viewport size.
    const source = local.playing && local.tracks.length ? 'LOCAL' : music.currentTrackType;
    const track = local.playing && local.tracks.length ? local.tracks[local.index] : music.currentSongName;
    const compactTrack = track.length > 18 ? `${track.slice(0, 17)}…` : track;
    return `▶ MUSIC · ${source} · ${compactTrack}`;
  }

  constructor() {
    super('Menu');
  }

  update(time: number): void {
    if (this.keyart) {
      const base = Math.max(GAME_WIDTH / this.keyart.width, GAME_HEIGHT / this.keyart.height);
      this.keyart.setScale(base * (1 + Math.sin(time / 9000) * 0.02));
      this.keyart.y = GAME_HEIGHT / 2 + Math.sin(time / 11000) * 8;
    }
    if (this.txtNowPlaying) {
      this.txtNowPlaying.setText(this.nowPlayingLabel());
    }
  }

  create(): void {
    // Keep the menu difficulty in sync with the profile so a saved slot and
    // the next deployment always describe the same challenge setting.
    this.difficulty = saveManager.data.difficulty ?? 'normal';
    void leaderboardService.syncLocalScores();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.closeUsernameModal();
      this.closeSaveGamesModal();
    });
    this.cameras.main.fadeIn(220, 0, 0, 0);
    // Quietly warm the shared tactical pack once the lightweight menu has
    // painted. Peak gameplay memory is unchanged because these are the same
    // global textures Battle uses; constrained/data-saver devices retain the
    // on-demand path instead of holding the pack while idle on the menu.
    this.time.delayedCall(350, () => {
      const nav = navigator as Navigator & {
        deviceMemory?: number;
        connection?: { saveData?: boolean };
      };
      const lowMemory = nav.deviceMemory !== undefined && nav.deviceMemory <= 4;
      const dataSaver = nav.connection?.saveData === true;
      const touchFirst = window.matchMedia?.('(pointer: coarse)').matches === true;
      if (!lowMemory && !dataSaver && !touchFirst) RuntimeAssetsScene.startInBackground(this);
    });
    const g = this.add.graphics();
    g.fillStyle(COLORS.bg, 1);
    g.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Key art background
    if (this.textures.exists('menu-keyart')) {
      const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'menu-keyart');
      const scale = Math.max(GAME_WIDTH / img.width, GAME_HEIGHT / img.height);
      img.setScale(scale).setAlpha(0.58);
      this.keyart = img;
      // Dark vignette / left calm zone for title
      g.fillGradientStyle(0x07090d, 0x07090d, 0x07090d, 0x07090d, 0.92, 0.55, 0.75, 0.92);
      g.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    } else {
      // Decorative grid fallback
      g.lineStyle(1, 0x121a20, 0.8);
      for (let x = 0; x < GAME_WIDTH; x += 48) g.lineBetween(x, 0, x, GAME_HEIGHT);
      for (let y = 0; y < GAME_HEIGHT; y += 48) g.lineBetween(0, y, GAME_WIDTH, y);
    }
    // Faint scanlines
    g.lineStyle(1, 0x000000, 0.06);
    for (let y = 0; y < GAME_HEIGHT; y += 4) g.lineBetween(0, y, GAME_WIDTH, y);

    // Retro vector corner brackets
    const B = 22;
    g.lineStyle(2, COLORS.accent, 0.5);
    g.lineBetween(0, 0, B, 0); g.lineBetween(0, 0, 0, B);
    g.lineBetween(GAME_WIDTH, 0, GAME_WIDTH - B, 0); g.lineBetween(GAME_WIDTH, 0, GAME_WIDTH, B);
    g.lineBetween(0, GAME_HEIGHT, B, GAME_HEIGHT); g.lineBetween(0, GAME_HEIGHT, 0, GAME_HEIGHT - B);
    g.lineBetween(GAME_WIDTH, GAME_HEIGHT, GAME_WIDTH - B, GAME_HEIGHT); g.lineBetween(GAME_WIDTH, GAME_HEIGHT, GAME_WIDTH, GAME_HEIGHT - B);

    addFullscreenIconButton(this, GAME_WIDTH - 48, 28);
    addAudioToggleButtons(this, 46, 28);

    if (this.textures.exists('command-emblem')) {
      this.add.image(GAME_WIDTH / 2, 30, 'command-emblem').setDisplaySize(42, 42).setAlpha(0.92);
    } else {
      this.add.text(GAME_WIDTH / 2, 28, '△', { fontFamily: 'Consolas, monospace', fontSize: '28px', color: '#5ec8ff' }).setOrigin(0.5);
    }
    g.fillStyle(COLORS.accent, 0.6);
    g.fillRect(GAME_WIDTH / 2 - 74, 52, 148, 1);

    // Now-playing card and commander telemetry echo the supplied command
    // bridge reference without stealing focus from deployment.
    g.fillStyle(0x0c151d, 0.9);
    g.fillRoundedRect(16, 58, 232, 54, 5);
    g.lineStyle(1, 0x31566a, 1);
    g.strokeRoundedRect(16, 58, 232, 54, 5);
    this.txtNowPlaying = this.add.text(28, 73, this.nowPlayingLabel(), {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#9ad4e8',
    }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
    this.txtNowPlaying.setData('baseColor', '#9ad4e8');
    this.txtNowPlaying.on('pointerover', () => this.txtNowPlaying?.setColor('#e4fbff'));
    this.txtNowPlaying.on('pointerout', () => this.txtNowPlaying?.setColor('#9ad4e8'));
    // Use pointer-up so the first menu gesture can unlock browser audio while
    // the shared welcome pointer-down listener still observes the click.
    this.txtNowPlaying.on('pointerup', () => {
      // The first menu click normally unlocks the welcome VO. A music-card
      // click is an intentional audio action, so it must stay silent.
      this.suppressNextMenuVoice = true;
      voice.stop();
      const local = localMusic.snapshot();
      if (local.playing && local.tracks.length > 1) localMusic.next();
      else music.nextSong();
      audio.click();
      this.txtNowPlaying?.setColor('#ffd27a');
      this.time.delayedCall(180, () => this.txtNowPlaying?.setColor('#9ad4e8'));
    });
    this.add.text(28, 98, 'CLICK TO CHANGE MUSIC', {
      fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#d4a24a',
    }).setOrigin(0, 0.5);

    // Accent bars
    g.fillStyle(COLORS.accent, 0.9);
    g.fillRect(GAME_WIDTH / 2 - 160, 118, 320, 2);
    g.fillStyle(COLORS.warn, 0.9);
    g.fillRect(GAME_WIDTH / 2 - 80, 124, 160, 2);

    this.add
      .text(GAME_WIDTH / 2, 80, 'AEGIS RESPONSE COMMAND', {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: '13px',
        color: '#6a7a88',
        letterSpacing: 4,
      })
      .setOrigin(0.5);

    this.add
      .text(GAME_WIDTH / 2, 150, 'APOGEE HOLD', {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: '48px',
        color: '#c8d4e0',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(GAME_WIDTH / 2, 200, 'Hold the colony. Break the mesh.', {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: '14px',
        color: '#5ec8ff',
      })
      .setOrigin(0.5);

    const rank = saveManager.data.rank;
    const rankBand = getCommandRank(rank);
    const xp = saveManager.data.xp;
    const commander = (saveManager.data.commanderName || 'ARC-CADET').slice(0, 15).toUpperCase();
    const best = saveManager.data.bestScores['kappa'] ?? 0;
    g.fillStyle(0x0c151d, 0.94);
    g.fillRoundedRect(GAME_WIDTH - 300, 12, 174, 54, 5);
    g.lineStyle(1, 0x31566a, 1);
    g.strokeRoundedRect(GAME_WIDTH - 300, 12, 174, 54, 5);
    this.add.text(GAME_WIDTH - 288, 23, `${rankBand.title}  ·  RANK ${rank}`, {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#c8d4e0',
    });
    this.add.text(GAME_WIDTH - 288, 40, `XP ${xp}   ·   DATA ${saveManager.data.researchPoints}`, {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#d4a24a',
    });
    this.add.text(GAME_WIDTH - 288, 53, `PERSONAL KAPPA  ${best || '—'}`, {
      fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6a7a88',
    });
    const profile = this.add.rectangle(GAME_WIDTH - 213, 84, 174, 34, 0x0c151d, 0.94)
      .setStrokeStyle(1, 0x31566a, 1)
      .setInteractive({ useHandCursor: true });
    this.add.text(GAME_WIDTH - 213, 79, `CALLSIGN  ${(saveManager.data.commanderName || 'ARC-CADET').slice(0, 15)}`, {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9ad4e8',
    }).setOrigin(0.5);
    this.add.text(GAME_WIDTH - 213, 91, 'EDIT / SWITCH PROFILE', {
      fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#d4a24a',
    }).setOrigin(0.5);
    profile.on('pointerover', () => profile.setFillStyle(0x1a2830));
    profile.on('pointerout', () => profile.setFillStyle(0x0c151d));
    profile.on('pointerdown', () => this.openUsernameModal(false));
    this.add
      .text(
        GAME_WIDTH / 2,
        250,
         `CALLSIGN ${commander}  ·  ${rankBand.title} ${rank}   XP ${xp}   DATA ${saveManager.data.researchPoints}\nPERSONAL KAPPA BEST ${best || '—'}`,
        {
          fontFamily: 'Consolas, Menlo, monospace',
          fontSize: '12px',
          color: '#6a7a88',
          align: 'center',
          lineSpacing: 6,
        },
      )
      .setOrigin(0.5);

    // Save data panel (3 slots). It starts dormant so the main menu stays
    // focused on deployment; SAVED GAMES toggles this rack into view.
    const saveCards = this.add.container(0, 0).setDepth(12).setVisible(false);
    this.saveCards = saveCards;
    const saveUi = this.add.graphics();
    saveCards.add(saveUi);
    const panelW = 560;
    const panelH = 152;
    const panelX = GAME_WIDTH / 2 - panelW / 2;
    const panelY = 318;
    saveUi.fillStyle(0x0e141a, 0.96);
    saveUi.fillRoundedRect(panelX, panelY, panelW, panelH, 8);
    saveUi.lineStyle(1, COLORS.panelEdge, 1);
    saveUi.strokeRoundedRect(panelX, panelY, panelW, panelH, 8);
    const cb = 10;
    saveUi.lineStyle(2, COLORS.accent, 0.4);
    saveUi.lineBetween(panelX, panelY, panelX + cb, panelY);
    saveUi.lineBetween(panelX, panelY, panelX, panelY + cb);
    saveUi.lineBetween(panelX + panelW, panelY, panelX + panelW - cb, panelY);
    saveUi.lineBetween(panelX + panelW, panelY, panelX + panelW, panelY + cb);
    saveUi.lineBetween(panelX, panelY + panelH, panelX + cb, panelY + panelH);
    saveUi.lineBetween(panelX, panelY + panelH, panelX, panelY + panelH - cb);
    saveUi.lineBetween(panelX + panelW, panelY + panelH, panelX + panelW - cb, panelY + panelH);
    saveUi.lineBetween(panelX + panelW, panelY + panelH, panelX + panelW, panelY + panelH - cb);

    saveCards.add(this.add.text(panelX + 14, panelY + 9, 'SAVE DATA', {
      fontFamily: 'Consolas, Menlo, monospace',
      fontSize: '13px',
      color: '#d4a24a',
      letterSpacing: 2,
    }));
    saveCards.add(this.add
      .text(panelX + panelW - 14, panelY + 12, 'progress auto-saves  ·  deletes ask first', {
        fontFamily: 'Consolas, monospace',
        fontSize: '9px',
        color: '#4a5560',
      })
      .setOrigin(1, 0));

    const slotW = 168;
    const slotGap = 12;
    const slotStartX = panelX + (panelW - (slotW * 3 + slotGap * 2)) / 2;
    saveManager.listSlots().forEach((slot, i) => {
      const sx = slotStartX + i * (slotW + slotGap);
      const sy = panelY + 34;
      const sh = panelH - 46;
      saveUi.fillStyle(0x121a20, 1);
      saveUi.fillRoundedRect(sx, sy, slotW, sh, 5);
      saveUi.lineStyle(1, slot.exists ? COLORS.accent : 0x2a3e4a, slot.exists ? 0.7 : 1);
      saveUi.strokeRoundedRect(sx, sy, slotW, sh, 5);

      saveCards.add(this.add.text(sx + 8, sy + 6, `SLOT ${i + 1}`, {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: slot.exists ? '#9ad4e8' : '#5a6672',
      }));
      const info = slot.exists
        ? `RANK ${slot.rank}   XP ${slot.xp}\nDIFFICULTY ${slot.difficulty.toUpperCase()}\nMISSIONS ${slot.missions}\n${this.formatSlotTime(slot.timestamp)}`
        : 'EMPTY';
      saveCards.add(this.add.text(sx + 8, sy + 25, info, {
        fontFamily: 'Consolas, monospace',
        fontSize: '9px',
        color: slot.exists ? '#6a7a88' : '#3a4550',
        lineSpacing: 3,
      }));

      const btnY = sy + sh - 15;
      const saveBtn = this.add
        .rectangle(sx + 8, btnY, 70, 22, 0x152028, 1)
        .setOrigin(0, 0.5)
        .setStrokeStyle(1, COLORS.accent)
        .setInteractive({ useHandCursor: true });
      saveCards.add(saveBtn);
      saveCards.add(this.add
        .text(sx + 43, btnY, 'SAVE', {
          fontFamily: 'Consolas, monospace',
          fontSize: '10px',
          color: '#5ec8ff',
        })
        .setOrigin(0.5));
      saveBtn.on('pointerover', () => saveBtn.setFillStyle(0x1a2830));
      saveBtn.on('pointerout', () => saveBtn.setFillStyle(0x152028));
      saveBtn.on('pointerdown', () => {
        saveManager.data.difficulty = this.difficulty;
        saveManager.saveToSlot(i);
        audio.click();
        this.scene.restart();
      });

      const loadBtn = this.add
        .rectangle(sx + 86, btnY, 70, 22, 0x152028, 1)
        .setOrigin(0, 0.5)
        .setStrokeStyle(1, slot.exists ? COLORS.accent : 0x2a3e4a)
        .setInteractive({ useHandCursor: slot.exists });
      saveCards.add(loadBtn);
      saveCards.add(this.add
        .text(sx + 121, btnY, 'LOAD', {
          fontFamily: 'Consolas, monospace',
          fontSize: '10px',
          color: slot.exists ? '#5ec8ff' : '#3a4550',
        })
        .setOrigin(0.5));
      loadBtn.on('pointerover', () => slot.exists && loadBtn.setFillStyle(0x1a2830));
      loadBtn.on('pointerout', () => loadBtn.setFillStyle(0x152028));
      loadBtn.on('pointerdown', () => {
        if (!slot.exists) return;
        if (saveManager.loadFromSlot(i)) {
          this.difficulty = saveManager.data.difficulty ?? 'normal';
          audio.click();
          this.scene.restart();
        }
      });

      if (slot.exists) {
        const del = this.add
          .text(sx + slotW - 12, sy + 5, '×', {
            fontFamily: 'Consolas, monospace',
            fontSize: '13px',
            color: '#6a7a88',
          })
          .setOrigin(1, 0)
          .setInteractive({ useHandCursor: true });
        saveCards.add(del);
        del.on('pointerover', () => del.setColor('#ff6a6a'));
        del.on('pointerout', () => del.setColor('#6a7a88'));
        del.on('pointerdown', () => {
          const confirmed = typeof window === 'undefined' || window.confirm(`Delete SAVE SLOT ${i + 1}?\n\nThis permanently removes mission progress, rank, research, awards and settings for this slot.`);
          if (!confirmed) return;
          saveManager.deleteSlot(i);
          audio.click();
          this.scene.restart();
        });
      }
    });

    // Difficulty
    const diffs: { id: 'normal' | 'hard' | 'nightmare'; label: string; sub: string }[] = [
      { id: 'normal', label: 'NORMAL', sub: '+40% hostile HP' },
      { id: 'hard', label: 'HARD', sub: '+150% hostile HP' },
      { id: 'nightmare', label: 'NIGHTMARE', sub: '+300% hostile HP' },
    ];
    diffs.forEach((d, i) => {
      const x = GAME_WIDTH / 2 - 130 + i * 130;
      const y = 496;
      const active = d.id === this.difficulty;
      const btn = this.add
        .rectangle(x, y, 120, 40, active ? 0x1a2830 : 0x152028, 1)
        .setStrokeStyle(1, active ? COLORS.accent : 0x2a3e4a)
        .setInteractive({ useHandCursor: true });
      btn.on('pointerover', () => btn.setFillStyle(0x1a2830));
      btn.on('pointerout', () => btn.setFillStyle(active ? 0x1a2830 : 0x152028));
      this.add
        .text(x, y - 7, d.label, {
          fontFamily: 'Consolas, monospace',
          fontSize: '12px',
          color: active ? '#5ec8ff' : '#6a7a88',
        })
        .setOrigin(0.5);
      this.add
        .text(x, y + 10, d.sub, {
          fontFamily: 'Consolas, monospace',
          fontSize: '9px',
          color: active ? '#9ad4e8' : '#4a5560',
        })
        .setOrigin(0.5);
      btn.on('pointerdown', () => {
        this.difficulty = d.id;
        saveManager.data.difficulty = d.id;
        saveManager.save();
        audio.click();
        this.scene.restart();
      });
    });

    // Deploy button
    const deploy = this.add
      .rectangle(GAME_WIDTH / 2, 580, 240, 48, 0x152028, 1)
      .setStrokeStyle(2, COLORS.accent)
      .setInteractive({ useHandCursor: true });
    addRetroButtonHover(deploy);
    this.add
      .text(GAME_WIDTH / 2, 580, 'DEPLOY  [ENTER]', {
        fontFamily: 'Consolas, monospace',
        fontSize: '16px',
        color: '#5ec8ff',
      })
      .setOrigin(0.5);
    deploy.on('pointerover', () => deploy.setFillStyle(0x1a2830));
    deploy.on('pointerout', () => deploy.setFillStyle(0x152028));
    deploy.on('pointerdown', () => this.startBattle());
    this.input.keyboard?.on('keydown-ENTER', () => this.startBattle());

    const menuAction = (x: number, label: string, action: () => void, enabled = true) => {
      const button = this.add.rectangle(x, 640, 120, 34, enabled ? 0x152028 : 0x10161c, 1)
        .setStrokeStyle(1.2, enabled ? COLORS.accent : 0x2a3e4a)
        .setInteractive({ useHandCursor: enabled });
      this.add.text(x, 640, label, { fontFamily: 'Consolas, monospace', fontSize: '10px', color: enabled ? '#5ec8ff' : '#4a5560' }).setOrigin(0.5);
      button.on('pointerover', () => { if (enabled) button.setFillStyle(0x1a2830); });
      button.on('pointerout', () => button.setFillStyle(enabled ? 0x152028 : 0x10161c));
      button.on('pointerdown', () => { if (enabled) { audio.click(); action(); } });
    };
    const mainActions: Array<[string, () => void, boolean?]> = [
      ['SAVED GAMES', () => this.toggleSaveCards()],
      ['CONTINUE', () => this.openSaveGamesModal()],
      ['SCORES', () => navigateTo(this, 'Command', { tab: 'leaderboards' })],
      ['SETTINGS', () => { this.scene.pause(); this.scene.launch('Pause', { from: 'Menu' }); }],
      ['EXIT GAME', () => exitGame(), true],
    ];
    const firstActionX = GAME_WIDTH / 2 - ((mainActions.length - 1) * 180) / 2;
    mainActions.forEach(([label, action, enabled], index) => menuAction(firstActionX + index * 180, label, action, enabled ?? true));

    this.add
      .text(
        GAME_WIDTH / 2,
        690,
        'Keys: 1-8 build · SPACE start/rush wave (auto waves) · Q spear · W barrier · E rolling death · R EMP · TAB mode · U/T/S/X tower · click tower for evolution',
        {
          fontFamily: 'Consolas, monospace',
          fontSize: '10px',
          color: '#4a5560',
          align: 'center',
        },
      )
      .setOrigin(0.5);

    this.add.text(16, GAME_HEIGHT - 12, 'A.Johanson · DEVELOPER', {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#3a4550',
    }).setOrigin(0, 1);

    g.fillStyle(0x071017, 0.68).fillRoundedRect(GAME_WIDTH - 142, GAME_HEIGHT - 28, 126, 20, 4);
    g.lineStyle(1, COLORS.panelEdge, 0.8).strokeRoundedRect(GAME_WIDTH - 142, GAME_HEIGHT - 28, 126, 20, 4);
    this.add
      .text(GAME_WIDTH - 16, GAME_HEIGHT - 12, `v${APP_VERSION} · PHASE 1`, {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: '#8aa8b5',
      })
      .setOrigin(1, 1);

    this.fieldManual = new FieldManualPanel(this, 130);
    this.input.keyboard?.on('keydown-H', () => this.fieldManual?.toggle());
    this.input.keyboard?.on('keydown-ESC', () => {
      this.scene.pause();
      this.scene.launch('Pause', { from: 'Menu' });
    });
    applyAudioSettings();
    applyUiTextScale(this);
    // Refresh restore must never steal the title screen on a normal return
    // (Pause → MAIN MENU, Command → MENU). Only an actual browser reload may
    // auto-advance, and even then never into Command — that board has no save
    // slots, so a redirect there blocks SAVE DATA / CONTINUE.
    const allowAutoResume = consumeSessionAutoResume() && this.wasDocumentReloaded();
    const resumeBattle = allowAutoResume && shouldAutoResumeSortie();
    rememberScene('Menu');
    const shouldShowIntro = typeof localStorage !== 'undefined' && localStorage.getItem(INTRO_SEEN_KEY) !== '1';
    if (shouldShowIntro) {
      this.time.delayedCall(320, () => this.openStory('intro', true));
    } else if (!saveManager.data.commanderName) {
      this.time.delayedCall(260, () => this.openUsernameModal(true));
    } else if (resumeBattle) {
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 80, 'RESTORING SORTIE CHECKPOINT…', {
        fontFamily: 'Consolas, monospace', fontSize: '12px', color: '#7dce5a',
      }).setOrigin(0.5).setDepth(200);
      this.time.delayedCall(200, () => navigateTo(this, 'Battle', { resume: true }));
    }

    // Voice: greeting plays on the first interaction (audio needs a gesture).
    // The now-playing card is an audio-only interaction and must not trigger
    // the greeting or talk over the newly selected track.
    const welcomePointer = (pointer: Phaser.Input.Pointer) => {
      const clickedNowPlaying = !!this.txtNowPlaying?.getBounds().contains(pointer.x, pointer.y);
      this.input.keyboard?.off('keydown', welcomeKey);
      if (clickedNowPlaying || this.suppressNextMenuVoice) {
        this.suppressNextMenuVoice = false;
        return;
      }
      voice.playCategory(this, 'welcome', { cooldown: 0 });
    };
    const welcomeKey = () => {
      if (this.suppressNextMenuVoice) {
        this.suppressNextMenuVoice = false;
        return;
      }
      voice.playCategory(this, 'welcome', { cooldown: 0 });
      this.input.off('pointerdown', welcomePointer);
    };
    this.input.once('pointerdown', welcomePointer);
    this.input.keyboard?.once('keydown', welcomeKey);
  }

  /**
   * True only when the document actually reloaded (refresh / restore).
   * In-session scene starts must not count, or Pause → MAIN MENU and
   * Command → MENU bounce straight back to the mission board.
   */
  private wasDocumentReloaded(): boolean {
    try {
      const nav = performance.getEntriesByType?.('navigation')?.[0] as { type?: string } | undefined;
      if (nav?.type === 'reload' || nav?.type === 'restore' || nav?.type === 'back_forward') return true;
      const prior = Number(sessionStorage.getItem('apogee-hold-menu-visits-v1') ?? '0');
      sessionStorage.setItem('apogee-hold-menu-visits-v1', String(prior + 1));
      // First Menu create after a fresh document counts as a cold boot.
      return prior === 0;
    } catch {
      return true;
    }
  }

  private openUsernameModal(required: boolean): void {
    this.closeUsernameModal();
    if (typeof document === 'undefined') return;
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(2,7,12,.78);font-family:Consolas,monospace;color:#d8f6ff;';
    const card = document.createElement('div');
    card.style.cssText = 'width:360px;padding:22px;background:#0b1720;border:1px solid #58c9e8;border-radius:8px;box-shadow:0 0 34px rgba(41,184,224,.22);';
    card.innerHTML = '<div style="font-size:14px;letter-spacing:1px;margin-bottom:8px">SELECT COMMAND PROFILE</div><div style="font-size:10px;color:#7da5b8;margin-bottom:14px">Choose a saved commander or enter a new callsign. A new callsign starts a fresh campaign; profiles keep their own progress on this browser.</div>';
    const profiles = saveManager.listProfiles();
    if (profiles.length > 0) {
      const profileSelect = document.createElement('select');
      profileSelect.style.cssText = 'box-sizing:border-box;width:100%;margin-bottom:9px;padding:9px 10px;background:#071017;border:1px solid #335563;color:#e5f8ff;font:12px Consolas,monospace;outline:none;';
      const newOption = document.createElement('option');
      newOption.value = '';
      newOption.textContent = 'NEW CALLSIGN / START FRESH';
      profileSelect.append(newOption);
      for (const profileName of profiles) {
        const option = document.createElement('option');
        option.value = profileName;
        option.textContent = profileName === saveManager.data.commanderName ? `${profileName} (CURRENT)` : profileName;
        profileSelect.append(option);
      }
      profileSelect.onchange = () => {
        const selected = profileSelect.value;
        if (!selected || !saveManager.loadProfile(selected)) return;
        audio.click();
        this.closeUsernameModal();
        this.scene.restart();
      };
      card.append(profileSelect);
    }
    const input = document.createElement('input');
    input.value = saveManager.data.commanderName || '';
    input.placeholder = 'e.g. LASER GOBLIN';
    input.maxLength = 18;
    input.type = 'text';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.style.cssText = 'box-sizing:border-box;width:100%;padding:9px 10px;background:#071017;border:1px solid #335563;color:#e5f8ff;font:12px Consolas,monospace;outline:none;';
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;margin-top:16px;';
    const makeButton = (label: string, primary = false) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.style.cssText = `padding:8px 14px;background:${primary ? '#1d4a5c' : '#152028'};border:1px solid ${primary ? '#64d7ff' : '#335563'};color:#d8f6ff;font:10px Consolas,monospace;cursor:pointer;`;
      return button;
    };
    const cancel = makeButton('CANCEL');
    const save = makeButton('SAVE CALLSIGN', true);
    const quick = makeButton('USE ARC-CADET');
    const commit = () => {
      const name = input.value.trim().replace(/\s+/g, ' ').replace(/[^a-zA-Z0-9 _-]/g, '').slice(0, 18);
      if (!name) { input.focus(); return; }
      saveManager.setCommanderName(name);
      this.closeUsernameModal();
      this.scene.restart();
    };
    quick.onclick = () => { input.value = 'ARC-CADET'; commit(); };
    save.onclick = commit;
    cancel.onclick = () => { if (!required) this.closeUsernameModal(); };
    input.onkeydown = (event) => {
      // Phaser listens for keyboard shortcuts on the document. Stop the
      // event at the callsign field so typing a name cannot trigger deploy,
      // wave or ability hotkeys. Only modal commands cancel the browser's
      // default key action; ordinary letters and numbers must remain editable.
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
      }
      if (event.key === 'Escape' && !required) {
        event.preventDefault();
        this.closeUsernameModal();
      }
    };
    actions.append(quick, cancel, save);
    card.append(input, actions);
    overlay.append(card);
    if (!required) overlay.onclick = (event) => { if (event.target === overlay) this.closeUsernameModal(); };
    document.body.append(overlay);
    this.usernameOverlay = overlay;
    this.usernameInput = input;
    input.focus();
    input.select();
  }

  private closeUsernameModal(): void {
    this.usernameOverlay?.remove();
    this.usernameOverlay = undefined;
    this.usernameInput = undefined;
  }

  private closeSaveGamesModal(): void {
    this.saveOverlay?.remove();
    this.saveOverlay = undefined;
  }

  private openStory(mode: 'intro' | 'credits', markSeen = false): void {
    if (mode === 'intro' && this.firstIntroOpened && markSeen) return;
    if (mode === 'intro') this.firstIntroOpened = true;
    audio.click();
    this.scene.pause();
    this.scene.launch('Story', { mode, returnTo: 'Menu', markSeen });
  }

  public showProfileAfterIntro(): void {
    if (!saveManager.data.commanderName) this.time.delayedCall(180, () => this.openUsernameModal(true));
  }

  private openSaveGamesModal(): void {
    this.closeSaveGamesModal();
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(2,7,12,.82);font-family:Consolas,monospace;color:#d8f6ff;';
    const card = document.createElement('div');
    card.style.cssText = 'width:min(560px,calc(100vw - 32px));max-height:min(78vh,560px);display:flex;flex-direction:column;padding:22px;background:#0b1720;border:1px solid #58c9e8;box-shadow:0 0 34px rgba(41,184,224,.22);';
    const heading = document.createElement('div');
    heading.textContent = 'SAVE GAMES';
    heading.style.cssText = 'font-size:17px;letter-spacing:2px;margin-bottom:4px;';
    const hint = document.createElement('div');
    hint.textContent = saveManager.data.activeRun ? 'CHECKPOINT READY · CONTINUE RESTORES YOUR LAST SORTIE' : 'SAVE PROFILE PROGRESS OR LOAD A PREVIOUS COMMAND DECK';
    hint.style.cssText = 'font-size:10px;color:#7da5b8;margin-bottom:16px;';
    const list = document.createElement('div');
    list.style.cssText = 'overflow-y:auto;display:flex;flex-direction:column;gap:10px;padding-right:5px;';
    const format = (ts: number) => ts ? new Date(ts).toLocaleString() : 'EMPTY SLOT';
    saveManager.listSlots().forEach((slot, index) => {
      const row = document.createElement('div');
      row.style.cssText = `display:flex;align-items:center;gap:12px;padding:12px;border:1px solid ${slot.exists ? '#31566a' : '#22333d'};background:#101c24;`;
      const copy = document.createElement('div');
      copy.style.cssText = 'flex:1;min-width:0;';
      copy.innerHTML = `<div style="font-size:13px;color:#dcefff">SLOT ${index + 1}</div><div style="font-size:10px;color:#6a9cad;margin-top:4px;">${slot.exists ? `RANK ${slot.rank} · XP ${slot.xp} · ${slot.missions} CLEARS · ${format(slot.timestamp)}` : 'NO PROFILE SAVED'}</div>`;
      const save = document.createElement('button');
      save.textContent = 'SAVE';
      save.style.cssText = 'border:1px solid #5ec8ff;background:#152b36;color:#aeeaff;padding:8px 13px;font:11px Consolas,monospace;cursor:pointer;';
      save.onclick = () => { saveManager.data.difficulty = this.difficulty; saveManager.saveToSlot(index); audio.click(); this.closeSaveGamesModal(); this.scene.restart(); };
      const load = document.createElement('button');
      load.textContent = 'LOAD';
      load.disabled = !slot.exists;
      load.style.cssText = `border:1px solid ${slot.exists ? '#d4a24a' : '#29383f'};background:#121d23;color:${slot.exists ? '#ffd27a' : '#4a5560'};padding:8px 13px;font:11px Consolas,monospace;cursor:${slot.exists ? 'pointer' : 'default'};`;
      load.onclick = () => { if (!slot.exists || !saveManager.loadFromSlot(index)) return; audio.click(); this.closeSaveGamesModal(); this.scene.restart(); };
      row.append(copy, save, load);
      list.append(row);
    });
    const autosave = document.createElement('div');
    autosave.style.cssText = 'display:flex;align-items:center;gap:12px;margin-top:14px;padding:12px;border:1px solid #496b52;background:#10241a;';
    const autosaveCopy = document.createElement('div');
    autosaveCopy.style.cssText = 'flex:1;min-width:0;';
    autosaveCopy.innerHTML = `<div style="font-size:13px;color:#b8f39d">AUTOSAVE CHECKPOINT</div><div style="font-size:10px;color:#78a77b;margin-top:4px;">${saveManager.data.activeRun ? `WAVE ${saveManager.data.activeRun.waveIndex} · ${saveManager.data.activeRun.missionId.toUpperCase()} · SAVED ${new Date(saveManager.data.activeRun.savedAt).toLocaleString()}` : 'NO ACTIVE SORTIE CHECKPOINT'}</div>`;
    const resume = document.createElement('button');
    resume.textContent = 'CONTINUE';
    resume.disabled = !saveManager.data.activeRun;
    resume.style.cssText = `border:1px solid ${saveManager.data.activeRun ? '#7dce5a' : '#293b30'};background:#173126;color:${saveManager.data.activeRun ? '#b8f39d' : '#4a6650'};padding:9px 16px;font:11px Consolas,monospace;cursor:${saveManager.data.activeRun ? 'pointer' : 'default'};`;
    resume.onclick = () => { if (!saveManager.data.activeRun) return; audio.click(); this.closeSaveGamesModal(); navigateTo(this, 'Battle', { resume: true }); };
    autosave.append(autosaveCopy, resume);
    const footer = document.createElement('div');
    footer.style.cssText = 'display:flex;justify-content:flex-end;margin-top:16px;';
    const close = document.createElement('button');
    close.textContent = 'CLOSE';
    close.style.cssText = 'border:1px solid #335563;background:#152028;color:#d8f6ff;padding:8px 16px;font:10px Consolas,monospace;cursor:pointer;';
    close.onclick = () => this.closeSaveGamesModal();
    footer.append(close);
    card.append(heading, hint, list, autosave);
    card.append(footer);
    overlay.append(card);
    overlay.onclick = (event) => { if (event.target === overlay) this.closeSaveGamesModal(); };
    document.body.append(overlay);
    this.saveOverlay = overlay;
  }

  private formatSlotTime(ts: number): string {
    if (!ts) return '';
    const d = new Date(ts);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private startBattle(): void {
    audio.unlock();
    applyAudioSettings();
    music.unlock();
    music.setMenuMode(true);
    music.setIntensity(0.05);
    navigateTo(this, 'Command');
  }
}

export class ResultsScene extends Phaser.Scene {
  constructor() {
    super('Results');
  }

  init(data: {
    win: boolean;
    score: number;
    integrity: number;
    maxIntegrity: number;
    waves: number;
    kills?: number;
    survival?: boolean;
    time?: number;
    missionName?: string;
    missionId?: string;
    nextMissionId?: string;
    difficulty?: 'normal' | 'hard' | 'nightmare';
    averageSpeed?: number;
    tempoBonus?: number;
    unspentCredits?: number;
    creditBonus?: number;
    integrityBonus?: number;
    mapDataAward?: number;
    xpEarned?: number;
    towerPlacements?: { id: string; x: number; y: number; stage: number }[];
  }): void {
    this.registry.set('results', data);
    music.setMenuMode(true);
  }

  create(): void {
    this.cameras.main.fadeIn(220, 0, 0, 0);
    const data = this.registry.get('results') as {
      win: boolean;
      score: number;
      integrity: number;
      maxIntegrity: number;
      waves: number;
      survival?: boolean;
      time?: number;
      missionName?: string;
      missionId?: string;
      nextMissionId?: string;
      difficulty?: 'normal' | 'hard' | 'nightmare';
      kills?: number;
      averageSpeed?: number;
      tempoBonus?: number;
      unspentCredits?: number;
      creditBonus?: number;
      integrityBonus?: number;
      mapDataAward?: number;
      xpEarned?: number;
      towerPlacements?: { id: string; x: number; y: number; stage: number }[];
    };
    const isSurv = !!data.survival;
    const title = isSurv ? 'SURVIVAL ENDED' : data.win ? 'MISSION COMPLETE' : 'COMMAND OVERRUN';
    const colour = data.win && !isSurv ? '#7dce5a' : '#ff4a4a';
    const stars = getClearStars(data.win && !isSurv, data.integrity, data.maxIntegrity);
    if (isSurv) voice.playCategory(this, 'resolution', { priority: true, cooldown: 0 });
    else if (data.win) voice.playCategory(this, 'victory', { priority: true, cooldown: 0 });
    else voice.playCategory(this, 'defeat', { priority: true, cooldown: 0 });

    const g = this.add.graphics();
    g.fillStyle(COLORS.bg, 1).fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    g.fillStyle(0x0b151c, 0.95).fillRoundedRect(72, 70, 1136, 580, 10);
    g.lineStyle(1.5, data.win ? COLORS.accent : COLORS.danger, 0.85).strokeRoundedRect(72, 70, 1136, 580, 10);
    g.lineStyle(1, COLORS.panelEdge, 0.8);
    for (let y = 82; y < 640; y += 6) g.lineBetween(84, y, 1196, y);
    g.lineStyle(2, COLORS.accent, 0.7);
    g.lineBetween(100, 94, 250, 94);
    g.lineBetween(1030, 94, 1180, 94);
    g.lineBetween(100, 626, 250, 626);
    g.lineBetween(1030, 626, 1180, 626);
    this.renderFieldRecordMap(g, data);

    const titleText = this.add.text(GAME_WIDTH / 2, 112, title, {
      fontFamily: 'Consolas, monospace', fontSize: '30px', color: colour, fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0);
    this.tweens.add({ targets: titleText, alpha: 1, y: 126, duration: 380, ease: 'Cubic.easeOut' });

    this.add.text(356, 178, data.missionName ?? 'APOGEE HOLD', {
      fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#8fa8b8',
    });
    this.add.text(356, 198, isSurv ? 'FIELD RECORD · SURVIVAL' : `FIELD RECORD · ${(data.difficulty ?? 'normal').toUpperCase()}`, {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#4a5560',
    });

    const starText = this.add.text(356, 238, formatClearStars(stars), {
      fontFamily: 'Consolas, monospace', fontSize: '40px', color: '#d4a24a', letterSpacing: 8,
    }).setAlpha(0);
    this.tweens.add({ targets: starText, alpha: 1, scale: { from: 0.7, to: 1 }, duration: 620, delay: 320, ease: 'Back.easeOut' });
    this.add.text(360, 288, stars === 3 ? 'PERFECT HOLD' : stars === 2 ? 'STRONG HOLD' : stars === 1 ? 'BASTION SURVIVED' : 'RECORD LOST', {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: stars ? '#d4a24a' : '#ff6a6a',
    });

    const scoreText = this.add.text(356, 330, 'SCORE  0', {
      fontFamily: 'Consolas, monospace', fontSize: '30px', color: '#e0e8f0', fontStyle: 'bold',
    });
    this.tweens.addCounter({
      from: 0,
      to: Math.floor(data.score),
      duration: 1100,
      ease: 'Cubic.easeOut',
      onUpdate: (tween) => {
        const value = Math.floor(tween.getValue() ?? 0);
        scoreText.setText(`SCORE  ${value.toLocaleString()}`);
        if (value > 0 && value % 100 < 8) audio.scoreTick();
      },
      onComplete: () => audio.scoreCommit(),
    });

    const mins = Math.floor((data.time ?? 0) / 60);
    const secs = Math.floor((data.time ?? 0) % 60);
    const avgSpeed = data.averageSpeed ?? 1;
    const missionWaves = getMissionWaves(data.missionId ?? 'kappa').length;
    this.add.text(360, 360, [
      `INTEGRITY      ${data.integrity} / ${data.maxIntegrity}`,
      `WAVES          ${data.waves}${isSurv ? '' : ` / ${missionWaves}`}`,
      `KILLS          ${data.kills ?? '—'}`,
      isSurv ? `TIME           ${mins}m ${String(secs).padStart(2, '0')}s` : '',
      `AVG SPEED      ${avgSpeed.toFixed(2)}x`,
      data.unspentCredits ? `CREDIT RESERVE  ${data.unspentCredits.toLocaleString()}  ·  +${(data.creditBonus ?? 0).toLocaleString()}` : '',
      data.integrityBonus ? `HOLD VALUE      +${data.integrityBonus.toLocaleString()}  ·  1,000 CR = 1 INTEGRITY` : '',
      data.mapDataAward ? `RESEARCH DATA   +${data.mapDataAward}  ·  CLEAR AWARD` : '',
      data.tempoBonus ? `TEMPO BONUS    +${data.tempoBonus.toLocaleString()}` : '',
      `COMMAND XP     +${data.xpEarned ?? Math.floor((data.score / 10) * XP_REWARD_MULTIPLIER)}`,
    ].filter(Boolean).join('\n'), {
      fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#c8d4e0', lineSpacing: 7,
    });

    const lbX = 700;
    g.fillStyle(0x101d25, 0.92).fillRoundedRect(lbX, 176, 470, 400, 8);
    g.lineStyle(1, COLORS.panelEdge, 1).strokeRoundedRect(lbX, 176, 470, 400, 8);
    this.add.text(lbX + 20, 194, 'LEADERBOARD', { fontFamily: 'Consolas, monospace', fontSize: '14px', color: '#5ec8ff' });
    const syncText = this.add.text(lbX + 20, 220, 'UPLOADING SCORE…', { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#6a7a88' });
    const boardText = this.add.text(lbX + 20, 250, 'MAP / OVERALL\nawaiting records', {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#c8d4e0', lineSpacing: 3,
    });

    const entry: LeaderboardEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      commander: saveManager.data.commanderName || 'ARC-CADET',
      mapId: data.missionId ?? 'kappa',
      mapName: data.missionName ?? 'APOGEE HOLD',
      difficulty: data.difficulty ?? 'normal',
      score: Math.floor(data.score),
      waves: data.waves,
      integrity: data.integrity,
      stars,
      timestamp: Date.now(),
    };
    // Render the just-finished run from the local profile immediately. The
    // hosted scoreboard is synced in the background, so a slow VPS never
    // leaves the player staring at an empty board.
    saveManager.recordLeaderboardEntry(entry);
    this.renderLeaderboardRows(boardText, {
      map: saveManager.getLeaderboard(entry.mapId, 10),
      overall: saveManager.getLeaderboard('overall', 10),
      source: 'local',
    }, entry.mapId);
    void leaderboardService.submit(entry).then((result) => {
      audio.leaderboard();
      syncText.setText(`${result.source === 'remote' ? 'SHARED' : 'LOCAL FALLBACK'} · MAP #${result.mapRank || '—'} · OVERALL #${result.overallRank || '—'}`).setColor(result.source === 'remote' ? '#7dce5a' : '#d4a24a');
      return leaderboardService.read(entry.mapId);
    }).then((snapshot) => this.renderLeaderboardRows(boardText, snapshot, entry.mapId));

    if (saveManager.data.settings.devMode) this.renderDevTuning();

    const nextMissionId = data.nextMissionId ?? getNextUnbeatenMissionId(saveManager.data.missionsComplete, saveManager.data.settings.devMode);
    const returnLabel = nextMissionId ? 'CONTINUE TO NEXT OPERATION' : 'RETURN TO COMMAND';
    const btn = this.add.rectangle(400, 610, 300, 42, 0x152028, 1).setStrokeStyle(2, COLORS.accent).setInteractive({ useHandCursor: true });
    addRetroButtonHover(btn);
    this.add.text(400, 610, returnLabel, { fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#5ec8ff' }).setOrigin(0.5);
    btn.on('pointerdown', () => { audio.click(); navigateTo(this, 'Command', { tab: 'mission', missionId: nextMissionId }); });
    const boardBtn = this.add.rectangle(850, 610, 260, 42, 0x152028, 1).setStrokeStyle(1.5, COLORS.supply).setInteractive({ useHandCursor: true });
    addRetroButtonHover(boardBtn);
    this.add.text(850, 610, 'OPEN LEADERBOARDS', { fontFamily: 'Consolas, monospace', fontSize: '12px', color: '#d4a24a' }).setOrigin(0.5);
    boardBtn.on('pointerdown', () => { audio.click(); navigateTo(this, 'Command', { tab: 'leaderboards' }); });
    this.input.keyboard?.on('keydown-ENTER', () => navigateTo(this, 'Command', { tab: 'mission', missionId: nextMissionId }));
    this.input.keyboard?.on('keydown-ESC', () => navigateTo(this, 'Command', { tab: 'mission', missionId: nextMissionId }));
  }

  private renderFieldRecordMap(g: Phaser.GameObjects.Graphics, data: {
    win: boolean;
    missionId?: string;
    missionName?: string;
    difficulty?: 'normal' | 'hard' | 'nightmare';
    towerPlacements?: { id: string; x: number; y: number; stage: number }[];
  }): void {
    const x = 96;
    const y = 164;
    const w = 220;
    const h = 152;
    if (!data.win) {
      if (this.textures.exists('unbeaten-door-graffiti')) {
        this.add.image(x + w / 2, y + h / 2 + 12, 'unbeaten-door-graffiti').setDisplaySize(136, 136).setAlpha(0.92).setDepth(3);
      }
      this.add.text(x + w / 2, y + h - 12, 'ARCHIVE SEALED · UNBEATEN', {
        fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#ff7474', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(5);
      return;
    }
    const mapId = data.missionId ?? 'kappa';
    const map = getMissionMap(mapId);
    const bgKey = `battle-${mapId}-bg`;
    if (this.textures.exists(bgKey)) this.add.image(x + w / 2, y + 62, bgKey).setDisplaySize(w, 124).setAlpha(0.9).setDepth(2);
    g.fillStyle(0x071017, 0.28).fillRect(x, y, w, 124);
    g.lineStyle(1.5, data.difficulty === 'hard' ? COLORS.supply : COLORS.accent, 0.9).strokeRect(x, y, w, 124);
    const mapG = this.add.graphics().setDepth(4);
    const sx = w / MAP_FRAME.width;
    const sy = 124 / MAP_FRAME.height;
    const project = (p: { x: number; y: number }) => ({ x: x + (p.x - MAP_FRAME.x) * sx, y: y + (p.y - MAP_FRAME.y) * sy });
    for (const route of getMapRoutes(map)) {
      const start = project(route[0]);
      mapG.lineStyle(3, 0x071017, 0.92).beginPath().moveTo(start.x, start.y);
      for (const point of route.slice(1)) { const q = project(point); mapG.lineTo(q.x, q.y); }
      mapG.strokePath();
      mapG.lineStyle(1.2, data.difficulty === 'hard' ? COLORS.supply : COLORS.accent, 0.95).beginPath().moveTo(start.x, start.y);
      for (const point of route.slice(1)) { const q = project(point); mapG.lineTo(q.x, q.y); }
      mapG.strokePath();
    }
    if (data.difficulty === 'hard') {
      for (const pad of map.pads) { const p = project(pad); mapG.lineStyle(1.5, COLORS.supply, 0.38).strokeCircle(p.x, p.y, 4); }
      for (const placement of data.towerPlacements ?? []) {
        const p = project(placement);
        const key = `top-${placement.id}`;
        if (this.textures.exists(key)) this.add.image(p.x, p.y, key).setDisplaySize(18, 18).setDepth(5);
        mapG.lineStyle(1, COLORS.supply, 0.9).strokeCircle(p.x, p.y, 10);
      }
      this.add.text(x, y + 130, 'HARD HOLD · TURRET PLACEMENT ARCHIVE', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#d4a24a' });
    } else {
      this.add.text(x, y + 130, 'CLEAN MAP ARCHIVE · HOLD RECORDED', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#5ec8ff' });
    }
    this.add.text(x, y - 14, `${data.missionName ?? map.name} · FIELD MAP`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#8fa8b8', fontStyle: 'bold' });
  }

  private renderDevTuning(): void {
    const x = 320; const y = 442; const w = 360; const h = 136;
    const g = this.add.graphics();
    g.fillStyle(0x101c22, 0.88).fillRoundedRect(x, y, w, h, 7);
    g.lineStyle(1, 0xd4a24a, 0.7).strokeRoundedRect(x, y, w, h, 7);
    this.add.text(x + 12, y + 10, 'DEV BALANCE · NEXT SORTIE', { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#d4a24a' });
    this.add.text(x + 12, y + 27, 'NEXT SORTIE BALANCE · HP / FORMATION', { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#a8bfca' });
    const setting = saveManager.data.settings;
    const button = (bx: number, by: number, bw: number, label: string, action: () => void, tone = 0x152028) => {
      const bg = this.add.rectangle(bx, by, bw, 22, tone, 1).setStrokeStyle(1, 0x5ec8ff).setInteractive({ useHandCursor: true });
      addRetroButtonHover(bg, tone);
      this.add.text(bx, by, label, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#bcecff' }).setOrigin(0.5);
      bg.on('pointerdown', () => { audio.click(); action(); });
    };
    const controlY = y + 50;
    button(x + 52, controlY, 72, 'HP −10%', () => { setting.devEnemyHpMultiplier = Phaser.Math.Clamp(Math.round((setting.devEnemyHpMultiplier - 0.1) * 100) / 100, 0.5, 2.5); saveManager.save(); this.scene.restart(); });
    button(x + 132, controlY, 72, 'HP +10%', () => { setting.devEnemyHpMultiplier = Phaser.Math.Clamp(Math.round((setting.devEnemyHpMultiplier + 0.1) * 100) / 100, 0.5, 2.5); saveManager.save(); this.scene.restart(); });
    button(x + 214, controlY, 72, 'SQUAD −', () => { setting.devSquadSize = Phaser.Math.Clamp((setting.devSquadSize ?? 1) - 1, 1, 4); saveManager.save(); this.scene.restart(); });
    button(x + 294, controlY, 72, 'SQUAD +', () => { setting.devSquadSize = Phaser.Math.Clamp((setting.devSquadSize ?? 1) + 1, 1, 4); saveManager.save(); this.scene.restart(); });

    const mapId = (this.registry.get('results') as { missionId?: string } | undefined)?.missionId ?? 'kappa';
    const laneCount = getMapRoutes(getMissionMap(mapId)).length;
    setting.devMapLaneWeights ??= {};
    const current = setting.devMapLaneWeights[mapId] ?? [];
    const weights = Array.from({ length: laneCount }, (_, index) => Math.max(0, Number(current[index] ?? 1)));
    this.add.text(x + 12, y + 78, `MAP WEIGHTING · ${mapId.toUpperCase()} LANES`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#d4a24a' });
    weights.forEach((weight, index) => {
      const cx = x + 78 + index * 92;
      this.add.text(cx, y + 96, `L${index + 1}  ${weight.toFixed(1)}`, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#c8d4e0' }).setOrigin(0.5);
      button(cx - 22, y + 118, 34, '−', () => this.adjustDevLaneWeight(mapId, index, -0.1));
      button(cx + 22, y + 118, 34, '+', () => this.adjustDevLaneWeight(mapId, index, 0.1));
    });
    this.add.text(x + 12, y + 130, `HP ×${setting.devEnemyHpMultiplier.toFixed(1)}  ·  SQUAD ×${setting.devSquadSize ?? 1}  ·  CREDITS ${Math.round(setting.devCreditMultiplier * 100)}%  ·  WEIGHTS AFFECT UNASSIGNED SPAWNS`, { fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#6f9eaf' });
  }

  private adjustDevLaneWeight(mapId: string, lane: number, delta: number): void {
    const setting = saveManager.data.settings;
    setting.devMapLaneWeights ??= {};
    const routeCount = getMapRoutes(getMissionMap(mapId)).length;
    const weights = Array.from({ length: routeCount }, (_, index) => Math.max(0, Number(setting.devMapLaneWeights[mapId]?.[index] ?? 1)));
    weights[lane] = Phaser.Math.Clamp(Math.round((weights[lane] + delta) * 10) / 10, 0, 5);
    setting.devMapLaneWeights[mapId] = weights;
    saveManager.save();
    this.scene.restart();
  }

  private renderLeaderboardRows(target: Phaser.GameObjects.Text, snapshot: LeaderboardSnapshot, mapId: string): void {
    const mapRows = snapshot.map.slice(0, 10).map((row, index) => `${String(index + 1).padStart(2, '0')}  ${row.commander.slice(0, 11).padEnd(11)} ${formatClearStars(row.stars)}  ${row.score.toLocaleString()}`);
    const overallRows = snapshot.overall.slice(0, 10).map((row, index) => `${String(index + 1).padStart(2, '0')}  ${row.mapName.slice(0, 12).padEnd(12)} ${row.score.toLocaleString()}`);
    target.setText([`MAP · ${mapId.toUpperCase()}`, ...(mapRows.length ? mapRows : ['-- no records --']), '', 'OVERALL', ...(overallRows.length ? overallRows : ['-- no records --'])].join('\n'));
  }
}
